import { VISUAL_CONFIG } from "@shared/config";
import * as THREE from "three";

/**
 * Formats a time in milliseconds to a MM:SS string.
 */
export function formatTime(timeMs: number): string {
  const minutes = Math.floor(timeMs / 60000);
  const seconds = Math.floor((timeMs % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Calculates the color for a team.
 */
export function getTeamColor(team: "A" | "B"): number {
  return team === "A"
    ? VISUAL_CONFIG.COLORS.TEAM_A
    : VISUAL_CONFIG.COLORS.TEAM_B;
}

/**
 * Calculates the position of a cell in the 3D world.
 */
export function getCellPosition(
  x: number,
  y: number
): { x: number; y: number; z: number } {
  return {
    x: x + 0.5, // Center of the cell
    y: 0, // Floor level
    z: y + 0.5, // Center of the cell (using y as z in our grid)
  };
}

/**
 * Determines if a point is within a cell.
 */
export function isPointInCell(
  pointX: number,
  pointY: number,
  cellX: number,
  cellY: number
): boolean {
  return (
    pointX >= cellX &&
    pointX < cellX + 1 &&
    pointY >= cellY &&
    pointY < cellY + 1
  );
}

/**
 * Calculates exponential backoff delay for reconnection attempts.
 * @param attempt Current reconnection attempt number.
 * @param baseDelay Base delay in milliseconds.
 * @param maxDelay Maximum delay in milliseconds.
 * @returns Delay in milliseconds.
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelay = 1000,
  maxDelay = 30000
): number {
  return Math.min(baseDelay * 1.5 ** attempt, maxDelay);
}

/**
 * Updates the mouth direction of a player mesh based on movement.
 * @param mesh The player mesh to update.
 * @param dx X-axis movement delta.
 * @param dy Y-axis movement delta.
 */
export function updateMouthDirection(
  mesh: THREE.Object3D,
  dx: number,
  dy: number
): void {
  if (!mesh.children || mesh.children.length <= 1) return;

  const mouth = mesh.children[1];

  if (Math.abs(dx) > Math.abs(dy)) {
    // Horizontal movement
    if (dx > 0) {
      // Moving right/east
      mouth.rotation.set(Math.PI / 2, -Math.PI / 2, 0);
    } else if (dx < 0) {
      // Moving left/west
      mouth.rotation.set(Math.PI / 2, Math.PI / 2, 0);
    }
  } else {
    // Vertical movement
    if (dy > 0) {
      // Moving down/south
      mouth.rotation.set(Math.PI / 2, 0, 0);
    } else if (dy < 0) {
      // Moving up/north
      mouth.rotation.set(Math.PI / 2, Math.PI, 0);
    }
  }
}

/**
 * Updates ghost eye positions based on movement direction.
 * @param leftPupil Left pupil mesh.
 * @param rightPupil Right pupil mesh.
 * @param dx X-axis movement delta.
 * @param dy Y-axis movement delta.
 */
export function updateGhostEyes(
  leftPupil: THREE.Object3D,
  rightPupil: THREE.Object3D,
  dx: number,
  dy: number
): void {
  if (Math.abs(dx) > Math.abs(dy)) {
    // Horizontal movement
    if (dx > 0) {
      // Moving right
      leftPupil.position.set(-0.12, 0.45, 0.32);
      rightPupil.position.set(0.18, 0.45, 0.32);
    } else {
      // Moving left
      leftPupil.position.set(-0.18, 0.45, 0.32);
      rightPupil.position.set(0.12, 0.45, 0.32);
    }
  } else {
    // Vertical movement
    if (dy > 0) {
      // Moving down
      leftPupil.position.set(-0.15, 0.45, 0.35);
      rightPupil.position.set(0.15, 0.45, 0.35);
    } else {
      // Moving up
      leftPupil.position.set(-0.15, 0.48, 0.32);
      rightPupil.position.set(0.15, 0.48, 0.32);
    }
  }
}

/**
 * Resets the material of a mesh to its original properties
 */
export function resetMeshMaterial(obj: THREE.Object3D): void {
  // Handle both Mesh and Group
  if (obj instanceof THREE.Mesh && obj.material) {
    if (Array.isArray(obj.material)) {
      for (const mat of obj.material) {
        if (mat.userData && mat.userData.originalOpacity !== undefined) {
          mat.opacity = mat.userData.originalOpacity;
          mat.transparent = mat.opacity < 1;
        }
      }
    } else {
      if (
        obj.material.userData &&
        obj.material.userData.originalOpacity !== undefined
      ) {
        obj.material.opacity = obj.material.userData.originalOpacity;
        obj.material.transparent = obj.material.opacity < 1;
      }
    }
  }

  // If it's a group, process all child meshes
  if (obj.children) {
    obj.children.forEach((child) => {
      if (child instanceof THREE.Mesh) {
        resetMeshMaterial(child);
      }
    });
  }
}

/**
 * Sets the opacity of a mesh and all its children
 */
export function setMeshOpacity(
  obj: THREE.Object3D,
  opacity: number,
  transparent: boolean
): void {
  // Handle mesh
  if (obj instanceof THREE.Mesh && obj.material) {
    if (Array.isArray(obj.material)) {
      for (const mat of obj.material) {
        if (mat.userData === undefined) mat.userData = {};
        if (mat.userData.originalOpacity === undefined) {
          mat.userData.originalOpacity = mat.opacity;
        }
        mat.opacity = opacity;
        mat.transparent = transparent;
      }
    } else {
      if (obj.material.userData === undefined) obj.material.userData = {};
      if (obj.material.userData.originalOpacity === undefined) {
        obj.material.userData.originalOpacity = obj.material.opacity;
      }
      obj.material.opacity = opacity;
      obj.material.transparent = transparent;
    }
  }

  // Process children if it's a group
  if (obj.children) {
    obj.children.forEach((child) => {
      if (child instanceof THREE.Mesh) {
        setMeshOpacity(child, opacity, transparent);
      }
    });
  }
}

/**
 * Updates the opacity of a mesh without storing the original value
 */
export function updateMeshOpacity(obj: THREE.Object3D, opacity: number): void {
  // Handle mesh
  if (obj instanceof THREE.Mesh && obj.material) {
    if (Array.isArray(obj.material)) {
      for (const mat of obj.material) {
        mat.opacity = opacity;
        mat.transparent = opacity < 1;
      }
    } else {
      obj.material.opacity = opacity;
      obj.material.transparent = opacity < 1;
    }
  }

  // Process children if it's a group
  if (obj.children) {
    obj.children.forEach((child) => {
      if (child instanceof THREE.Mesh) {
        updateMeshOpacity(child, opacity);
      }
    });
  }
}

export function lerp(start: number, end: number, t: number): number {
  return start + t * (end - start);
}

/**
 * Calculates the distance between two points
 */
export function calculateDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}
